import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Save, ChevronLeft, ChevronRight } from "lucide-react";

import { API_URL as API } from "../../config";

export default function AttendancePage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [athletes, setAthletes] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [records, setRecords] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const a = t.admin;

  useEffect(() => {
    const loadAthletes = async () => {
      const res = await authFetch(`${API}/api/admin/athletes`);
      if (res.ok) setAthletes((await res.json()).filter(a => a.status === "active"));
    };
    loadAthletes();
  }, [authFetch]);

  useEffect(() => {
    const loadAttendance = async () => {
      const res = await authFetch(`${API}/api/admin/attendance?date=${date}`);
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(r => { map[r.athlete_id] = r.status; });
        setRecords(map);
      }
    };
    loadAttendance();
    setSaved(false);
  }, [date, authFetch]);

  const changeDate = (days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().split("T")[0]);
  };

  const toggleStatus = (athleteId) => {
    setRecords(prev => {
      const current = prev[athleteId] || "unmarked";
      const next = current === "present" ? "absent" : current === "absent" ? "excused" : "present";
      return { ...prev, [athleteId]: next };
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const bulkRecords = athletes.map(a => ({ athlete_id: a.id, status: records[a.id] || "unmarked" })).filter(r => r.status !== "unmarked");
    if (bulkRecords.length > 0) {
      await authFetch(`${API}/api/admin/attendance/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, records: bulkRecords }),
      });
    }
    setSaving(false);
    setSaved(true);
  };

  const stats = useMemo(() => {
    const present = Object.values(records).filter(s => s === "present").length;
    const absent = Object.values(records).filter(s => s === "absent").length;
    const excused = Object.values(records).filter(s => s === "excused").length;
    return { present, absent, excused, total: athletes.length };
  }, [records, athletes]);

  const statusColors = { present: "bg-emerald-500 text-white", absent: "bg-red-500 text-white", excused: "bg-amber-500 text-white", unmarked: "bg-gray-200 text-gray-600" };

  return (
    <div data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.attendance}</h2>
          <p className="text-gray-500 text-sm">{a.trackAttendance}</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-attendance-btn"
          className="flex items-center gap-2 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black px-5 py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? a.saving : saved ? a.saved : a.saveAttendance}
        </button>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => changeDate(-1)} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="prev-date"><ChevronLeft className="w-5 h-5" /></button>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="attendance-date"
          className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:outline-none focus:border-[#87CEEB]" />
        <button onClick={() => changeDate(1)} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="next-date"><ChevronRight className="w-5 h-5" /></button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl p-4 border text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.present}</p>
          <p className="text-xs text-gray-500">{a.present}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border text-center">
          <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
          <p className="text-xs text-gray-500">{a.absent}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border text-center">
          <p className="text-2xl font-bold text-amber-600">{stats.excused}</p>
          <p className="text-xs text-gray-500">{a.excused}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.total}</p>
          <p className="text-xs text-gray-500">{a.total}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {athletes.length === 0 ? (
          <div className="p-12 text-center text-gray-400">{a.noActiveAthletesFirst}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {athletes.map(at => {
              const st = records[at.id] || "unmarked";
              return (
                <div key={at.id} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`attendance-row-${at.id}`}>
                  <div>
                    <p className="font-medium text-gray-900">{at.name}</p>
                    <p className="text-xs text-gray-400">#{at.jersey_number || "-"} &middot; {at.position || "Unassigned"}</p>
                  </div>
                  <button onClick={() => toggleStatus(at.id)} data-testid={`toggle-${at.id}`}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${statusColors[st]}`}>
                    {st}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
